import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-video-widget.st.css';
import {DoppeActionTypeVideo, doppeActionTypeVideoResolveSettingsFromBoundEntity} from '../doppe-action-type-video';
import {evaluateFunction} from '@wix/devzai-utils-common';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {doppeHideableValueIsVisibleAndNotEqualValue} from '../../../client-server-common/types/doppe-hideable-value';
import {
    bymoPageContextPropsResolveBusinessEntity,
    IDoppeViewerActionContext,
    useBymoPageContextProps
} from '../../../doppe-sdk';
import {
    doppeBusinessEntityDtoIsNotFound,
    doppeBymoPageVideoSourceResolveVideoUrl,
    doppeExternalVideoProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    BymoPageViewerMode,
    useBymoPageViewerContext
} from '../../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {
    DoppeDtoActionViewType,
    doppeDtoActionViewTypeIsDisplayedAsButton
} from '../../../client-server-common/types/doppe-dto-action';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppeViewerVideoPlayer} from '../../../components/doppe-viewer-video-player/doppe-viewer-video-player';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';

export const DoppeActionTypeVideoWidget = React.memo(function DoppeActionTypeVideoWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeVideo.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const viewType = actionContext.getActionViewType();
    const pageViewerContext = useBymoPageViewerContext();
    const actionSettings = actionContext.action.settings;
    const isRenderedOnPage = actionContext.getActionViewType() === DoppeDtoActionViewType.InPageFrameless


    const showActionHeader = useMemo(() => {
        return actionContext.action.showActionHeader !== undefined ? actionContext.action.showActionHeader : true;
    }, [actionContext.action]);


    const {
        muteVideo,
        autoplay
    } = evaluateFunction(() => {

        if (pageViewerContext.mode === BymoPageViewerMode.Preview) {
            return {
                muteVideo: true,
                autoplay: false
            }
        } else if (doppeDtoActionViewTypeIsDisplayedAsButton(viewType)) {
            return {
                muteVideo: false,
                autoplay: true
            }
        } else {

            const mutedAutoplay = actionSettings.videoPlayerSettings.autoplay;

            return {
                muteVideo: mutedAutoplay,
                autoplay: mutedAutoplay
            }
        }
    })

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeVideoBindableWidget
                actionContext={actionContext}
                reportGoalAchieved={(goalType) => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    })
                }}
                showActionHeader={showActionHeader}
                actionId={actionContext.action.id}
                actionName={actionContext.action.name}
                actionSettings={{
                    ...actionContext.action.settings,
                    videoPlayerSettings: {
                        ...actionContext.action.settings.videoPlayerSettings,
                        autoplay: autoplay,
                        muteVideo: muteVideo
                    }
                }}
                isRenderedOnPage={isRenderedOnPage}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeVideoBindableWidget = React.memo(function DoppeActionTypeVideoBindableWidget(
    props: {
        actionSettings: DoppeActionTypeVideo.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionName: string;
        showActionHeader?: boolean;
        isRenderedOnPage: boolean;
        actionContext: IDoppeViewerActionContext<any>;
        listItemId?: string;
    }
) {
    const {
        actionContext,
        actionSettings,
        reportGoalAchieved,
        actionId,
        actionName,
        isRenderedOnPage,
        showActionHeader = true,
        listItemId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();

    const {
        videoProvider,
        videoProviderId
    } = actionSettings;


    if (videoProvider !== null && videoProviderId !== null) {
        const boundService = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalVideoProviderResolveBusinessEntityReference(videoProvider, videoProviderId))

        if (!boundService || doppeBusinessEntityDtoIsNotFound(boundService)) {
            return (
                <DoppeActionTypeVideoWidgetContentUnavailable
                    reason={
                        !boundService ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            const resolvedActionSettings = {
                ...actionSettings,
                ...doppeActionTypeVideoResolveSettingsFromBoundEntity(actionSettings, boundService)
            };
            const videoTitle = doppeHideableValueIsVisibleAndNotEqualValue(resolvedActionSettings.title, '') ? resolvedActionSettings.title : actionName;

            return (
                <DoppeActionTypeVideoWidgetInner
                    actionContext={actionContext}
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={resolvedActionSettings}
                    videoTitle={videoTitle}
                    showActionHeader={showActionHeader}
                    isRenderedOnPage={isRenderedOnPage}
                    listItemId={listItemId}
                />
            )
        }
    } else {
        const videoTitle = doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.title, '') ? actionSettings.title : actionName;

        return (
            <DoppeActionTypeVideoWidgetInner
                actionContext={actionContext}
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
                videoTitle={videoTitle}
                showActionHeader={showActionHeader}
                isRenderedOnPage={isRenderedOnPage}
                listItemId={listItemId}
            />
        )
    }
});


export const DoppeActionTypeVideoWidgetContentUnavailable = React.memo(function DoppeActionTypeServiceWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitleService']}
        />
    )
});


const DoppeActionTypeVideoWidgetInner = React.memo(function DoppeActionTypeVideoWidgetInner(
    props: {
        isRenderedOnPage: boolean;
        actionSettings: DoppeActionTypeVideo.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        videoTitle?: string;
        showActionHeader?: boolean;
        actionContext: IDoppeViewerActionContext<any>;
        listItemId?: string;
    }
) {

    const {
        actionSettings,
        actionContext,
        reportGoalAchieved,
        videoTitle,
        isRenderedOnPage,
        showActionHeader = true,
        listItemId
    } = props;

    const actionTitle = videoTitle;

    const {
        description,
        title,
        externalVideoUrl,
        videoPlayerSettings: {
            autoplay,
            muteVideo,
        },
        ctaButtons,
    } = actionSettings;

    const showInnerContent =
        doppeHideableValueIsVisibleAndNotEqualValue(title, '') ||
        doppeHideableValueIsVisibleAndNotEqualValue(description, '') ||
        ctaButtons.filter(ctaButton => ctaButton.isVisible).length > 0;

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
            actionContext={actionContext}
            reportGoalAchieved={reportGoalAchieved}
            fullWidthMainMedia={true}
            mainMedia={<DoppeViewerVideoPlayer
                className={style(
                    classes.videoContent,
                    {
                        isRenderedOnPage: isRenderedOnPage,
                        showInnerContent: showInnerContent,
                        showActionHeader: showActionHeader
                    }
                )}
                videoResource={externalVideoUrl}
                muteVideo={muteVideo}
                autoplay={autoplay}
                videoTitle={actionTitle}
                onStartPlaying={() => reportGoalAchieved(DoppeActionGoalType.PlayVideo)}
            />}
            sections={actionSettings.sections}
            buttons={actionSettings.ctaButtons}
            buttonsSettings={{
                sourceURL: doppeBymoPageVideoSourceResolveVideoUrl(externalVideoUrl),
                listItemId: listItemId,
            }}
        >
            <DoppeActionTypeWidgetInnerTitle>
                {title}
            </DoppeActionTypeWidgetInnerTitle>
            <DoppeActionTypeWidgetInnerDescription>
                {description}
            </DoppeActionTypeWidgetInnerDescription>
        </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
    );
});

